import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaPaperPlane, FaBars, FaSpinner } from 'react-icons/fa';
import { MapPin } from 'lucide-react';
import api from '../../api/axiosConfig';
import { formatMessageTime, groupMessagesByDay, formatDayLabel } from '../../utils/smartTime';
import { useTranslation } from 'react-i18next';
import { stringToColor }  from '../../utils/avatarColor';

const getCurrentUserId = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user && typeof user.id === 'number') return user.id;
    } catch (e) {
      console.warn('Failed to parse user from localStorage');
    }
  }
  return 1;
};

export default function MessagingLayout() {
  const { id: conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const ws = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [landlord, setLandlord] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); 
  const [isLoading, setIsLoading] = useState(false);
  const [, setIsTyping] = useState(false);
  const location = useLocation();

  const currentUserId = getCurrentUserId();
  const { t } = useTranslation('Messages');  
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
     
  const markConversationAsRead = useCallback(
    async (convId) => {
      try {
        await api.post(`/conversations/${convId}/mark_read/`);
        setConversations((prev) =>
          prev.map((conv) => (conv.id === convId ? { ...conv, unread_count: 0 } : conv))
        );
      } catch (err) {
        console.warn('Failed to mark conversation as read:', err);
      }
    },[]);

    useEffect(() => {
      if (conversationId && messages.length > 0 && activeConversation) {
        if (activeConversation.unread_count > 0) {
          markConversationAsRead(conversationId);
        }
      }
    }, [conversationId, messages.length, activeConversation, markConversationAsRead]);

  const fetchConversationsList = useCallback(async () => {
    try {
      const res = await api.get('/conversations/');
      setConversations(res.data);
      return res.data;
    } catch (err) {
      console.error('Failed to refresh conversations:', err);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchConversationsList();
  }, [fetchConversationsList]);

  // Fetch conversations and auto-redirect if on /messages (no ID)
  useEffect(() => {
  if (location.pathname === '/messages') {
    (async () => {
      try {
        const data = await fetchConversationsList();
        if (data && data.length > 0) {
          const latest = data[0];
          navigate(`/messages/${latest.id}`, { replace: true });
        }
      } catch (err) {
        console.error('Error fetching conversations:', err);
      }
    })();
  }
}, [location.pathname, navigate, fetchConversationsList]);

  // Fetch active conversation + messages
  useEffect(() => {
  if (!conversationId) {
    setActiveConversation(null);
    setMessages([]);
    setLandlord(null);
    return;
  }

  const fetchActive = async () => {
    setIsLoading(true);
    try {
      await fetchConversationsList();
      const convRes = await api.get(`/conversations/${conversationId}/`);
      setActiveConversation(convRes.data);

      const other =
        convRes.data.participants.find((p) => p.id !== currentUserId) ||
        convRes.data.participants[0];
      setLandlord(other);

      const msgRes = await api.get(`/conversations/${conversationId}/messages/`);
      setMessages(msgRes.data.reverse());
    } catch (err) {
      console.error('Fetch error:', err);
      setActiveConversation(null);
      setMessages([]);
      setError('Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  };

  fetchActive();
}, [conversationId, currentUserId, markConversationAsRead, fetchConversationsList]); 

  // Auto-hide typing indicator
  useEffect(() => {
    if (newMessage.trim()) {
      setIsTyping(true);
      const timer = setTimeout(() => setIsTyping(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [newMessage]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);


  const buildWebSocketUrl = (conversationId) => {
  const loc = window.location;
  const protocol = loc.protocol === 'https:' ? 'wss' : 'ws';
    const host = process.env.REACT_APP_WS_HOST || loc.host;
    return `${protocol}://${host}/ws/chat/${conversationId}/`;
  };
   
  // WebSocket
  useEffect(() => {
    if (!conversationId) return;

    const wsUrl = buildWebSocketUrl(conversationId);
    ws.current = new WebSocket(wsUrl);

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'chat_message') {
            const msg = data.message;
            const senderId = typeof msg.sender === 'string' ? parseInt(msg.sender, 10) : msg.sender;
            const convoId = data.conversation_id;

            // If in active conversation, add message
            if (String(convoId) === conversationId) {
              setMessages(prev => {
                // Avoid duplicates by id
                if (prev.some(m => m.id === msg.id)) return prev;
                return [...prev, msg];
              });
            }


            // If message is from someone else
            if (senderId !== currentUserId) {
              setConversations(prev => {
                const existingIndex = prev.findIndex(c => c.id === convoId);
                
                if (existingIndex >= 0) {
                  // Existing conversation: increment unread if not active
                  const updated = [...prev];
                  if (String(convoId) !== conversationId) {
                    updated[existingIndex] = {
                      ...updated[existingIndex],
                      unread_count: (updated[existingIndex].unread_count || 0) + 1,
                      last_message: msg,
                      updated_at: new Date().toISOString()
                    };
                  }
                  return updated;
                } else {
                  // 🆕 NEW conversation: we don't have it in the list!
                  // Instead of guessing, trigger a full refetch
                  fetchConversationsList(); // 👈 We’ll define this
                  return prev;
                }
              });
            }
          }
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };
    return () => {
      ws.current?.close(1000, 'Unmounted');
    };
  }, [conversationId, currentUserId, fetchConversationsList]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;

    setIsSending(true);
    const textToSend = newMessage;
    setNewMessage('');

    try {
      await api.post(`/conversations/${conversationId}/send_message/`, { text: textToSend });
      // Do NOT setMessages here – WebSocket will add the real message
    } catch (err) {
      console.error('Send failed:', err);
      alert(t('messages.status.failedSend'));
      // Optionally restore the text in the input:
      setNewMessage(textToSend);
    } finally {
      setIsSending(false);
    }
  };


  const selectConversation = (convId) => {
    navigate(`/messages/${convId}`);
    setSidebarOpen(false);
  };

  // === Message Actions ===
  const openContextMenu = useCallback((e, message) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      message,
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY,
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text).catch(console.error);
    closeContextMenu();
  };

  const deleteMessage = async (msgId) => {
    try {
      await api.delete(`/conversations/${conversationId}/messages/${msgId}/`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (err) {
      alert(t('messages.status.failedDelete'));
      console.error(err);
    }
    closeContextMenu();
  };


  // === Responsive Logic ===
  const hasConversations = conversations.length > 0;
  const showChat = conversationId || (!isMobile && hasConversations);
  
  const inputBottomPx = isMobile ? 6 : 24; 
  const rootPaddingClass = isMobile ? 'pb-24' : ''; 
  const messagesExtraPad = isMobile ? 'pb-28' : ''; 

  if (error) {
    return <div className="flex items-center justify-center h-screen text-red-500">{t('messages.status.error')}</div>;
  }

  const renderConversationList = () => (
    <div className="flex-1 overflow-y-auto py-2">
      {conversations.map((conv) => {
        const other = conv.participants.find(p => p.id !== currentUserId);
        if (!other) return null;
        const isActive = String(conv.id) === conversationId;
        return (
          <div
            key={conv.id}
            onClick={() => selectConversation(conv.id)}
            className={`flex items-center gap-3 p-2 top-10 cursor-pointer transition-all ${
              isActive ? 'bg-white/10 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.1)]' : 'hover:bg-white/10 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.1)]'
            }`}
          >
            {(() => {
              const displayName = other.first_name || other.username || 'U';
              const initials = displayName.charAt(0).toUpperCase();
              const bgColor = stringToColor(other.username || other.id || 'user').substring(1);
              const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bgColor}&color=fff&size=150`;
              return (
                <img
                  src={avatarUrl}
                  alt={other.name || other.username}
                  className="w-10 h-10 rounded-full object-cover"
                />
              );
            })()}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{other.name || other.username}</h3>
              {conv.last_message && (
                <p className="text-sm text-gray-400 truncate">
                  {conv.last_message.sender === currentUserId ? 'You: ' : ''}
                  {conv.last_message.text}
                </p>
              )}
            </div>
            {conv.unread_count > 0 && (
              <span className="bg-pink-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                1
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div 
      className={`flex flex-col md:flex-row h-screen bg-[var(--bg-black)] text-[var(--text-black)] w-full relative ${rootPaddingClass}`}
      onClick={closeContextMenu}
    >
      {/* Desktop Sidebar */}
      {!isMobile && (
      <div className="w-80 z-40 flex flex-col border-r border-[var(--border)] bg-[var(--bg-black)]">
        {renderConversationList()} 
      </div>
    )}

      {/* Mobile Drawer */}
      {isMobile && sidebarOpen && (
        <div className="fixed inset-0 z-40 flex">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative w-64 h-full bg-[var(--bg-black)] shadow-[inset_-1px_0_0_0_rgba(255,255,255,0.1)] z-40">
            <div className="flex justify-between items-center p-3 border-b border-[var(--border)]">
              <h2 className="font-semibold">{t('messages.navigation.chats')}</h2>
              <button onClick={() => setSidebarOpen(false)} className="text-pink-500 font-bold bg-white/10 px-3 py-2 rounded-full border-none">
                ✕
              </button>
            </div>
            {renderConversationList()}
          </div>
        </div>
      )}

      {/* Main Chat */}
      {showChat ? (
        activeConversation ? (
          <div className="flex flex-col flex-1 h-full">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-[var(--bg-black)] border-b border-[var(--border)] p-3 flex items-center gap-3">
              {isMobile && (
                <>
                  <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-full hover:bg-white/10 bg-transparent border-none text-[var(--text-black)]">
                    <FaBars size={20} />
                  </button>
                </>
              )}
              {(() => {
                const displayName = landlord?.first_name || landlord?.username || 'U';
                const initials = displayName.charAt(0).toUpperCase();
                const bgColor = stringToColor(landlord?.username || landlord?.id || 'user').substring(1);
                const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bgColor}&color=fff&size=150`;
                return (
                  <img
                    src={avatarUrl}
                    alt={landlord?.name || 'User'}
                    className="w-10 h-10 rounded-full ring-2 ring-pink-500 object-cover"
                  />
                );
              })()}
              <div>
                <h2 className="font-semibold">{landlord?.name || t('messages.ui.user')}</h2>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <MapPin size={12} />
                  <span>{activeConversation.property_title || t('messages.ui.property')}</span>
                </div>
              </div>
            </header>

            {/* Messages */}
            <div className={`flex-1 overflow-y-auto p-4 space-y-4 relative ${messagesExtraPad}`}>
              {isLoading ? (
                <div className="flex justify-center my-8">
                  <div>{t('messages.status.loading')}</div>
                </div>
              ) : (
                groupMessagesByDay(messages).map((group) => {
                  const dayLabel = formatDayLabel(group.day);
                  return (
                    <React.Fragment key={group.day}>
                      <div className="flex justify-center my-3">
                        <span className="text-xs text-gray-500 bg-[var(--bg-black)] px-3 py-1 rounded-full border border-[var(--border)]">
                          {dayLabel}
                        </span>
                      </div>
                      {group.messages.map((msg) => {
                        const isUser = msg.sender === currentUserId;
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                            onContextMenu={(e) => openContextMenu(e, msg)}
                            onTouchStart={(e) => {
                              // Optional: long press for mobile context menu
                              // For simplicity, we'll rely on tap-and-hold via browser or use tap for now
                            }}
                          >
                            <div
                              className={`max-w-xs sm:max-w-md px-4 py-2.5 rounded-2xl ${
                                isUser
                                  ? 'bg-pink-700 text-white rounded-br-none'
                                  : 'bg-white/10 text-[var(--text-black)] rounded-bl-none'
                              } relative`}
                            >
                              <p className="text-sm">{msg.text}</p>
                              <span className={`text-[9px] mt-1 block ${isUser ? 'text-pink-200' : 'text-gray-400'}`}>
                                {formatMessageTime(msg.created_at)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="sticky  bg-[var(--bg-black)] p-3  border-[var(--border)] z-30" style={{ bottom: `${inputBottomPx}px `}}>
              <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('messages.ui.placeholder')}
                  className="flex-1 px-4 py-2.5 rounded-full bg-[var(--bg-black)] border border-[var(--border)] border-solid text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || isSending}
                  className="p-2.5 bg-pink-500 text-white border-none rounded-full hover:bg-pink-600 disabled:opacity-50 transition send-button"
                >
                  {isSending ? <FaSpinner className="animate-spin" size={16} /> : <FaPaperPlane size={16} />}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500" />
        )
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
          <div className="text-gray-400 mb-4">{t('messages.status.noMessages')}</div>

        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-gray-800 border border-gray-700 rounded-md shadow-lg text-white text-sm"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
            minWidth: '140px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => copyMessage(contextMenu.message.text)}
            className="block w-full text-left px-4 py-2 hover:bg-gray-700"
          >
            {t('messages.actions.copy')}
          </button>
          {contextMenu.message.sender === currentUserId && (
            <button
              onClick={() => deleteMessage(contextMenu.message.id)}
              className="block w-full text-left px-4 py-2 hover:bg-gray-700 text-red-400"
            >
              {t('messages.actions.delete')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}